/**
 * Cockpit API client.
 *
 * Talks to the Cyrano FastAPI backend via fetch. Base URL comes from
 * VITE_API_BASE (env). In prod, the cockpit is served same-origin behind
 * a reverse proxy that injects auth, so VITE_API_BASE is empty and the
 * X-Admin-Token header is omitted.
 *
 * In dev, set VITE_ADMIN_TOKEN to bypass the proxy step.
 */

import { signalerRequeteReussie } from "@/core/api/ecritureAuFan";
import { estEnDemo } from "@/core/demo/actif";
import {
  INTROUVABLE,
  PASSER,
  TRADUCTION_INCONNUE,
  repondreEnDemo,
} from "@/core/demo/serveur";

const RAW_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");
const ADMIN_TOKEN = import.meta.env.VITE_ADMIN_TOKEN ?? "";

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** FastAPI body.detail extracted pour usage direct (alert / inline error). */
  detail: string | null;
  /** Code machine quand le backend renvoie un detail structuré
   *  `{ code, message, doc_url }` (ex: "bot_not_connected"). Permet à l'UI
   *  de rendre une bannière dédiée + lien doc au lieu d'un texte brut. */
  code: string | null;
  /** URL de doc cliquable associée au code (peut être null). */
  docUrl: string | null;
  constructor(message: string, status: number, body: unknown) {
    // FastAPI renvoie { detail: "..." } sur HTTPException. On le sort
    // dans `.detail` ET on l'utilise comme message principal si présent
    // — sinon les `error.message` UI affichent "HTTP 422" générique
    // alors que le serveur sait pourquoi exactement.
    const detail = extractDetail(body);
    const structured = extractStructuredDetail(body);
    super(detail ? `${detail} (HTTP ${status})` : message);
    this.status = status;
    this.body = body;
    this.detail = detail;
    this.code = structured?.code ?? null;
    this.docUrl = structured?.docUrl ?? null;
    this.name = "ApiError";
  }
}

/**
 * Levée quand un fetch dépasse son `timeout` (l'AbortController interne
 * d'`apiRequest`/`apiRequestBlob`). Remplace le message brut du navigateur
 * — "signal is aborted without reason" — par un texte lisible. Le `name`
 * est stable pour que l'UI puisse détecter le cas (ex: avertir l'opérateur
 * de vérifier la conversation avant de relancer un envoi potentiellement
 * déjà parti côté serveur).
 */
export class TimeoutError extends Error {
  timeoutMs: number;
  constructor(timeoutMs: number) {
    super(
      `Délai dépassé : le serveur n'a pas répondu en ${Math.round(timeoutMs / 1000)}s. ` +
        "Vérifie ta connexion et réessaie.",
    );
    this.timeoutMs = timeoutMs;
    this.name = "TimeoutError";
  }
}

function extractDetail(body: unknown): string | null {
  if (body && typeof body === "object" && "detail" in body) {
    const d = (body as { detail: unknown }).detail;
    if (typeof d === "string") return d;
    // Detail structuré : { code, message, doc_url } (envoi bloqué côté
    // backend). On prend `message` comme texte principal.
    if (d && typeof d === "object" && "message" in d) {
      const m = (d as { message: unknown }).message;
      if (typeof m === "string") return m;
    }
    // FastAPI validation error renvoie une list[dict] dans detail
    if (Array.isArray(d) && d.length > 0) {
      const first = d[0];
      if (first && typeof first === "object" && "msg" in first) {
        return String((first as { msg: unknown }).msg);
      }
    }
  }
  return null;
}

/** Extrait le detail structuré `{ code, message, doc_url }` quand présent.
 *  Renvoie null pour les erreurs string/validation classiques. */
function extractStructuredDetail(
  body: unknown,
): { code: string; docUrl: string | null } | null {
  if (body && typeof body === "object" && "detail" in body) {
    const d = (body as { detail: unknown }).detail;
    if (d && typeof d === "object" && !Array.isArray(d) && "code" in d) {
      const code = (d as { code: unknown }).code;
      if (typeof code === "string" && code) {
        const rawUrl = (d as { doc_url?: unknown }).doc_url;
        const docUrl = typeof rawUrl === "string" && rawUrl ? rawUrl : null;
        return { code, docUrl };
      }
    }
  }
  return null;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** Query params appended to URL (skipped when undefined / null) */
  params?: Record<string, string | number | boolean | null | undefined>;
  /** JSON body — auto-stringified, sets Content-Type */
  json?: unknown;
  /** Raw body (FormData, Blob, etc.) — n'ajoute pas Content-Type
   *  (le browser le force avec le boundary multipart). */
  body?: BodyInit | null;
  /** Timeout in ms (default 20s) */
  timeout?: number;
  /** Bearer token (WorkOS JWT) injected as `Authorization: Bearer <token>` */
  bearerToken?: string | null;
}

export function buildUrl(path: string, params?: RequestOptions["params"]): string {
  const baseOrigin = RAW_BASE || window.location.origin;
  const url = new URL(path, baseOrigin.endsWith("/") ? baseOrigin : `${baseOrigin}/`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, json, body, timeout = 20_000, headers, bearerToken, ...rest } = options;
  if (json !== undefined && body !== undefined && body !== null) {
    throw new Error("apiRequest: pass either `json` or `body`, not both");
  }

  // 🎬 Mode démo : la surface des conversations est servie en local, et
  // AUCUNE requête de cette surface ne part sur le réseau — c'est ce qui rend
  // le tournage sûr. Le reste du cockpit (créatrices, Vault, catalogue) passe
  // par ici sans changement. Cf `core/demo/serveur.ts`.
  if (estEnDemo()) {
    const reponse = await repondreEnDemo(path, {
      methode: (rest.method ?? "GET").toUpperCase(),
      params,
      json,
      // 🪤 Un envoi multipart passe par `body`, pas par `json` : la transaction
      // AVEC aperçu (`send-payment-link-with-upload`) arrivait donc vide côté
      // démo — bouton « Payer 0 € », et aucun montant à encaisser.
      formulaire:
        typeof FormData !== "undefined" && body instanceof FormData ? body : undefined,
    });
    if (reponse === INTROUVABLE) {
      // La démo connaît la surface mais pas CETTE route : on répond 404, comme
      // le ferait un Cyrano pas encore déployé. Le lecteur prend son chemin
      // « la route n'est pas là » au lieu de recevoir un objet mal formé.
      throw new ApiError(`HTTP 404 Not Found`, 404, {
        detail: "route absente du mode démo",
      });
    }
    if (reponse === TRADUCTION_INCONNUE) {
      // La démo n'a pas de traducteur : elle ne connaît que les phrases que
      // Francis propose. Une phrase libre le DIT, plutôt que de partir en
      // français déguisée en traduction.
      throw new ApiError(`HTTP 422`, 422, {
        detail: "en démo, seules les phrases proposées par Francis se traduisent",
      });
    }
    if (reponse !== PASSER) {
      // ⏱️ Un envoi joué remet le chrono du verrou à l'heure, comme un vrai
      // (#1180) : sinon les discussions tombaient à 15 min en plein tournage.
      signalerRequeteReussie(path, rest.method ?? "GET");
      return reponse as T;
    }
  }
  const controller = new AbortController();
  // `didTimeout` distingue notre abort de timeout d'un autre AbortError
  // éventuel, pour ne convertir QUE le timeout en TimeoutError lisible.
  let didTimeout = false;
  const timer = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeout);

  // Pour les bodies multipart (FormData), on NE FORCE PAS Content-Type :
  // le browser génère lui-même `multipart/form-data; boundary=...`.
  const isRawBody = body !== undefined && body !== null;

  try {
    const resp = await fetch(buildUrl(path, params), {
      ...rest,
      signal: controller.signal,
      credentials: rest.credentials ?? "include",
      headers: {
        ...(isRawBody ? {} : { "Content-Type": "application/json" }),
        ...(ADMIN_TOKEN ? { "X-Admin-Token": ADMIN_TOKEN } : {}),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...headers,
      },
      body: isRawBody
        ? (body as BodyInit)
        : json !== undefined
          ? JSON.stringify(json)
          : undefined,
    });

    if (!resp.ok) {
      let errBody: unknown = null;
      try {
        errBody = await resp.json();
      } catch {
        try { errBody = await resp.text(); } catch { /* nothing */ }
      }
      throw new ApiError(`HTTP ${resp.status} ${resp.statusText}`, resp.status, errBody);
    }

    // Un envoi à un fan remet le chrono du chatteur à l'heure (cf ecritureAuFan).
    signalerRequeteReussie(path, rest.method ?? "GET");

    if (resp.status === 204) return undefined as T;
    const ct = resp.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) return (await resp.json()) as T;
    return (await resp.text()) as unknown as T;
  } catch (err) {
    // Notre timeout abort le signal → fetch rejette avec un AbortError au
    // message cryptique ("signal is aborted without reason"). On le traduit
    // en TimeoutError lisible. Les ApiError (réponse !ok) et les erreurs
    // réseau passent telles quelles.
    if (didTimeout || (err instanceof DOMException && err.name === "AbortError")) {
      throw new TimeoutError(timeout);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Variante d'`apiRequest` qui retourne un Blob (pour download de PDF /
 * autres binaires). Mêmes conventions auth/credentials que `apiRequest`,
 * mais ne tente pas de parser la réponse en JSON. Erreur HTTP → ApiError.
 */
export async function apiRequestBlob(
  path: string,
  options: RequestOptions = {},
): Promise<Blob> {
  const { params, json, body, timeout = 30_000, headers, bearerToken, ...rest } = options;
  if (json !== undefined && body !== undefined && body !== null) {
    throw new Error("apiRequestBlob: pass either `json` or `body`, not both");
  }
  const controller = new AbortController();
  let didTimeout = false;
  const timer = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeout);
  const isRawBody = body !== undefined && body !== null;
  try {
    const resp = await fetch(buildUrl(path, params), {
      ...rest,
      signal: controller.signal,
      credentials: rest.credentials ?? "include",
      headers: {
        ...(isRawBody ? {} : { "Content-Type": "application/json" }),
        ...(ADMIN_TOKEN ? { "X-Admin-Token": ADMIN_TOKEN } : {}),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...headers,
      },
      body: isRawBody
        ? (body as BodyInit)
        : json !== undefined
          ? JSON.stringify(json)
          : undefined,
    });
    if (!resp.ok) {
      let errBody: unknown = null;
      try {
        errBody = await resp.json();
      } catch {
        try { errBody = await resp.text(); } catch { /* nothing */ }
      }
      throw new ApiError(`HTTP ${resp.status} ${resp.statusText}`, resp.status, errBody);
    }
    return await resp.blob();
  } catch (err) {
    if (didTimeout || (err instanceof DOMException && err.name === "AbortError")) {
      throw new TimeoutError(timeout);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Timeouts par type d'opération. Le défaut 20s d'`apiRequest` convient aux
// GET légers, mais les mutations qui orchestrent des services externes lents
// (création d'un lien de paiement Dropp + envoi Telegram, parfois un album +
// pré-création des livraisons) le dépassent régulièrement — l'abort produit
// alors le cryptique "signal is aborted without reason". Les uploads multipart
// (jusqu'à 50 Mo de média à pousser vers le backend puis ré-envoyer à Telegram)
// ont besoin d'encore plus de marge. Un appelant peut toujours override en
// passant `options.timeout` (le spread `...options` vient après le défaut).
const POST_TIMEOUT_MS = 60_000;
const UPLOAD_TIMEOUT_MS = 120_000;

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "json" | "body">) =>
    apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, json?: unknown, options?: Omit<RequestOptions, "method" | "json" | "body">) =>
    apiRequest<T>(path, { timeout: POST_TIMEOUT_MS, ...options, method: "POST", json }),
  /** POST qui retourne un Blob (PDF, etc.). Headers Content-Disposition
   *  préservés sur le `resp.blob()` — l'appelant peut lire le filename
   *  via un `fetch` direct s'il en a besoin. */
  postForBlob: (
    path: string,
    json?: unknown,
    options?: Omit<RequestOptions, "method" | "json" | "body">,
  ) => apiRequestBlob(path, { timeout: POST_TIMEOUT_MS, ...options, method: "POST", json }),
  /** Multipart upload : pass FormData. Le browser génère Content-Type avec
   *  le bon boundary, on ne le force pas dans les headers. */
  postForm: <T>(path: string, body: FormData, options?: Omit<RequestOptions, "method" | "json" | "body">) =>
    apiRequest<T>(path, { timeout: UPLOAD_TIMEOUT_MS, ...options, method: "POST", body }),
  put: <T>(path: string, json?: unknown, options?: Omit<RequestOptions, "method" | "json" | "body">) =>
    apiRequest<T>(path, { timeout: POST_TIMEOUT_MS, ...options, method: "PUT", json }),
  patch: <T>(path: string, json?: unknown, options?: Omit<RequestOptions, "method" | "json" | "body">) =>
    apiRequest<T>(path, { timeout: POST_TIMEOUT_MS, ...options, method: "PATCH", json }),
  delete: <T>(path: string, options?: Omit<RequestOptions, "method" | "json" | "body">) =>
    apiRequest<T>(path, { ...options, method: "DELETE" }),
  /** Alias historique de `postForm` — POST multipart/form-data binary upload. */
  upload: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, "method" | "json" | "body">) =>
    apiRequest<T>(path, { timeout: UPLOAD_TIMEOUT_MS, ...options, method: "POST", body: formData }),
};
